import { inject, injectable, named } from '../shared/inversify';
import { ContributionProvider } from './contribution-provider';
import { Logger } from './logger';
import type { Disposable } from './disposable';

export const ErrorHandlerContribution = Symbol.for('ErrorHandlerContribution');

export interface ErrorHandlerContribution {
    registerHandler(handlers: ErrorHandlerRegistry): void;
}

/**
 * Represents a handler function that can handle an error.
 * @param error - The error to be handled.
 * @returns True if the error was handled successfully, false otherwise.
 */
export type Handler = (error: Error) => boolean;

export const ErrorHandlerRegistry = Symbol.for('ErrorHandlerRegistry');

export interface ErrorHandlerRegistry {
    register(handler: Handler): Disposable;
    onStart(): void;
    readonly handlers: Handler[];
}

/**
 * Represents an implementation of the ErrorHandlerRegistry interface.
 * This class is responsible for managing error handlers and their contributions.
 */
@injectable()
export class ErrorHandlerRegistryImpl implements ErrorHandlerRegistry {
    private _handlers: Handler[] = [];

    /**
     * Creates an instance of ErrorHandlerRegistryImpl.
     * @param _contributionProvider The contribution provider for error handler contributions.
     * @param _logger The logger instance for logging warnings.
     */
    constructor(
        @inject(ContributionProvider) @named(ErrorHandlerContribution)
        private readonly _contributionProvider: ContributionProvider<ErrorHandlerContribution>,
        @inject(Logger)
        private readonly _logger: Logger
    ) {
        //
    }

    /**
     * Called when the registry starts.
     * Iterates through the contributions and registers the handlers.
     */
    onStart(): void {
        this._contributionProvider.getContributions().forEach(contribution => {
            contribution.registerHandler(this);
        });
    }

    /**
     * Registers a handler with the registry.
     * @param handler The handler to be registered.
     * @returns A disposable object that can be used to unregister the handler.
     */
    register(handler: Handler): Disposable {
        if (this._handlers.indexOf(handler) === -1) {
            this._handlers.push(handler);
        } else {
            this._logger.warn('The handler has been added', handler);
        }

        return {
            dispose: () => {
                const index = this._handlers.indexOf(handler);
                if (index !== -1) {
                    this._handlers.splice(index, 1);
                }
            },
        };
    }

    /**
     * Gets the list of registered handlers.
     * @returns An array of registered handlers.
     */
    get handlers() {
        return [...this._handlers];
    }
}
